import type { ReturnObject } from "../types/returnObject.ts";
import { ErrorType } from "../types/returnObject.ts";
import type { Session, ImpersonationState } from "../types/session.ts";
import type { ThemePack } from "../providers/themeProvider.tsx";
import { Revision as clientVersion} from "../revisionInfo.ts"

export interface AppConfig {
  HostAddress: string;
  SignInServiceUrl: string;
  WindowTitle: string;
  /**
   * Default theme pack for this hostname. Resolved per-request by the pod's
   * nginx from HOST_OVERRIDES; first-time visitors who haven't picked a pack
   * in localStorage get this one. Invalid values / unsubstituted placeholders
   * fall back to "instantsystems" client-side.
   */
  ThemePack?: ThemePack | string;
  /**
   * Per-host logo override. Empty strings or unsubstituted placeholders fall
   * back to PACK_LOGOS[pack][variant] in `logo.tsx`.
   */
  Logo?: { light?: string; dark?: string; mark?: string } | null;
}

/** Strings that mean "no value was substituted" — treat as absent. */
function isPlaceholder(s: string | undefined | null): boolean {
  return !s || s.startsWith("__");
}

let cachedConfig: AppConfig | null = null;
let sessionAccessor: (() => Session | null) | null = null;
let impersonationAccessor: (() => ImpersonationState | null) | null = null;
/** Reason describing how the session ended, surfaced to the user-facing sign-in banner. */
export type SessionEndedReason = "refresh-failed" | "session-expired";
let onSessionExpiredCallback: ((reason: SessionEndedReason) => void) | null = null;
let onUserQueryCallback: ((userQuery: string, method: string, args?: Record<string, unknown>) => void) | null = null;

export async function loadConfig(): Promise<AppConfig> {
  const response = await fetch(`${import.meta.env.BASE_URL}config.json`);
  if (!response.ok) {
    throw new Error(`Failed to load config: ${response.status}`);
  }
  const config = (await response.json()) as AppConfig;
  cachedConfig = config;
  document.title = getBrandTitle();
  return config;
}

/**
 * Resolved brand title for the current host. Read at render time wherever a
 * "Instant Cockpit"-style label appears so the value flips with the host pack.
 * Falls back to "Instant Cockpit" if the config never loaded or carries an
 * unsubstituted `__WINDOW_TITLE__` placeholder.
 */
export function getBrandTitle(): string {
  try {
    const t = getConfig().WindowTitle;
    if (!isPlaceholder(t)) return t;
  } catch { /* config not loaded yet */ }
  return "Instant Cockpit";
}

/**
 * Returns the API URL prefix: `${HostAddress}${BASE_URL_no_trailing_slash}`.
 * Used by every fetch / XHR that targets the AdminServerService backend so
 * all paths get the deployment's base prefix (e.g. `/beta`) without callers
 * having to know about it. `path` MUST start with `/`.
 */
export function buildApiUrl(path: string): string {
  const host = getConfig().HostAddress.replace(/\/+$/, "");
  const base = import.meta.env.BASE_URL.replace(/\/+$/, "");
  return `${host}${base}${path}`;
}

export function getConfig(): AppConfig {
  if (!cachedConfig) {
    throw new Error("Config not loaded. Call loadConfig() first.");
  }
  return cachedConfig;
}

export function setSessionAccessor(fn: () => Session | null): void {
  sessionAccessor = fn;
}

export function setImpersonationAccessor(fn: (() => ImpersonationState | null) | null): void {
  impersonationAccessor = fn;
}

export function setOnSessionExpired(fn: (reason: SessionEndedReason) => void): void {
  onSessionExpiredCallback = fn;
}

/** Fire the session-ended hook from outside the request path. Used by the JWT wake-up refresh
 *  in `cockpitJwt.ts`, which can discover a dead refresh chain with no call in flight. */
export function notifySessionExpired(reason: SessionEndedReason): void {
  onSessionExpiredCallback?.(reason);
}

export function setOnUserQuery(fn: ((userQuery: string, method: string, args?: Record<string, unknown>) => void) | null): void {
  onUserQueryCallback = fn;
}

export function buildAuthHeader(session: Session): string {
  return `UID= ${session.UID}|ActiveEndpointID=${session.ActiveEndPointID}|Session=${session.Session}|Ticket=${session.Ticket}|ClientVersion=${clientVersion}`;
}

/* ── Active-endpoint guards ────────────────────────────────────────────
 *
 * The active endpoint lives in mutable global state — `sessionRef` for the
 * legacy pipe-Auth path, the `activeEndpoint_<uid>` localStorage key for the
 * JWT path. Every call resolves it when the fetch is *built*, which is after
 * the caller's await points, not when the caller decided to call. So a call
 * requested while endpoint A was active can be sent after the user switched
 * away — most visibly a fire-and-forget follow-up fired from the continuation
 * of a slow read (see `markAgentResultSeen` in cockpitApi.ts), which lands
 * long after its page unmounted.
 *
 * `switchEndpoint()` parks the session on ActiveEndPointID 0. Server-side that
 * resolves to an endpoint with no connection string, and the first thing to
 * touch it is the permission attribute — `UserPermission.GetItems` blows up
 * inside the SQL lookup rather than returning a clean error.
 *
 * Two guards close this:
 *   - `assertEndpointForMethod` refuses to send an endpoint-scoped method
 *     while the resolved endpoint is 0.
 *   - `bumpEndpointEpoch()` aborts everything in flight whenever the active
 *     endpoint changes, so a response from the old endpoint can never land in
 *     the new endpoint's UI.
 */

/** Methods that are meaningful with no endpoint picked — the `<MethodNoPermission>`
 *  server methods that never touch endpoint-scoped data. `WebInitCache` is
 *  deliberately absent: it carries that attribute but does need a real endpoint,
 *  so a WebInitCache with endpoint 0 is a bug worth surfacing. */
const ENDPOINT_AGNOSTIC_METHODS: ReadonlySet<string> = new Set([
  "webgetuserendpoints",
  "webgetsharedlink",
  "uploadclienterror",
  "serversideclientinfo",
  "testtoken",
  "endsession",
  "debuginfo",
  "webissuefileaccesstoken",
  "webrefreshfileaccesstoken",
]);

/** Thrown instead of sending an endpoint-scoped call while no endpoint is active. */
export class NoActiveEndpointError extends Error {
  readonly method: string;
  constructor(method: string) {
    super(`Suppressed "${method}": no active endpoint`);
    this.name = "NoActiveEndpointError";
    this.method = method;
  }
}

function assertEndpointForMethod(method: string, endpointId: number | undefined): void {
  if (endpointId && endpointId > 0) return;
  if (ENDPOINT_AGNOSTIC_METHODS.has(method.toLowerCase())) return;
  if (__DEV_FEATURES__) {
    // eslint-disable-next-line no-console
    console.warn(
      `[servicePost] Suppressed "${method}" — no active endpoint.\n` +
      `A call was built after the active endpoint was cleared (endpoint switch, sign-out).\n` +
      `Usually a continuation of an already-resolved read firing a follow-up call after its page unmounted.`
    );
  }
  throw new NoActiveEndpointError(method);
}

/** True for the two "this call was intentionally dropped" outcomes: an abort (endpoint
 *  change or the caller's own signal) and a suppressed no-endpoint call. Error-logging
 *  catch blocks should stay quiet for these — an endpoint switch is a routine abort path,
 *  not a failure. */
export function isDroppedCallError(err: unknown): boolean {
  if (err instanceof NoActiveEndpointError) return true;
  return typeof err === "object" && err !== null && (err as { name?: string }).name === "AbortError";
}

let endpointEpoch = 0;
let endpointAbort = new AbortController();
const epochListeners = new Set<() => void>();

/** Current endpoint generation. Increments on every active-endpoint change. */
export function getEndpointEpoch(): number {
  return endpointEpoch;
}

/** Abort every in-flight backend call and open a new epoch. Called by AuthProvider
 *  whenever the active endpoint changes — switch, pick, logout, session expiry.
 *  Call it *before* firing any request that belongs to the new endpoint. */
export function bumpEndpointEpoch(): void {
  endpointEpoch++;
  const prev = endpointAbort;
  endpointAbort = new AbortController();
  prev.abort();
  for (const fn of [...epochListeners]) {
    try { fn(); } catch { /* a listener must not block the rest */ }
  }
}

/** Subscribe to endpoint-epoch changes. Returns an unsubscribe function. */
export function onEndpointEpochChange(fn: () => void): () => void {
  epochListeners.add(fn);
  return () => { epochListeners.delete(fn); };
}

/** Compose the caller's signal (if any) with the current epoch's abort signal. */
function withEndpointSignal(signal?: AbortSignal): AbortSignal {
  const epochSignal = endpointAbort.signal;
  if (!signal) return epochSignal;
  const anyOf = (AbortSignal as unknown as { any?: (s: AbortSignal[]) => AbortSignal }).any;
  if (typeof anyOf === "function") return anyOf([signal, epochSignal]);
  const ac = new AbortController();
  if (signal.aborted || epochSignal.aborted) {
    ac.abort();
  } else {
    const forward = () => ac.abort();
    signal.addEventListener("abort", forward, { once: true });
    epochSignal.addEventListener("abort", forward, { once: true });
  }
  return ac.signal;
}

/**
 * Compute the auth headers for an outbound request, branching JWT vs legacy. Use this for raw
 * fetch / XHR calls that bypass servicePost — file upload/download, direct binary endpoints,
 * etc. Async because the JWT path may need a transparent /oauth/refresh round-trip first.
 *
 * Returns either `{ Auth }` (legacy pipe-Auth string) or
 * `{ Authorization, "X-Active-Endpoint" }` (Bearer + endpoint header). Caller spreads the
 * returned map into their own headers object — both shapes are flat string maps.
 */
export async function buildAuthHeaders(): Promise<Record<string, string>> {
  const { getTokens, getAccessToken, getActiveEndpointId } = await import("../auth/cockpitJwt.ts");
  const tokens = getTokens();
  let headers: Record<string, string>;
  if (tokens) {
    const access = await getAccessToken();
    if (!access) {
      onSessionExpiredCallback?.("refresh-failed");
      throw new Error("Cockpit JWT refresh failed");
    }
    const endpointId = getActiveEndpointId();
    assertEndpointForMethod("(raw request)", endpointId);
    headers = {
      Authorization: `Bearer ${access}`,
      "X-Active-Endpoint": String(endpointId),
    };
  } else {
    const session = sessionAccessor?.();
    if (!session) throw new Error("No active session");
    assertEndpointForMethod("(raw request)", session.ActiveEndPointID);
    headers = { Auth: buildAuthHeader(session) };
  }
  applyImpersonationHeader(headers);
  return headers;
}

function buildServiceUrl(path: string): string {
  return buildApiUrl(path);
}

export function handleCommonErrors<T>(result: ReturnObject<T>): void {
  if (result === null || !result.bError) return;
    switch (result.eErrorType) {
      case ErrorType.SessionExpired:
        onSessionExpiredCallback?.("session-expired");
        break;
      case ErrorType.PermissionDenied:
        console.warn("Permission denied:", result.sMessage);
        break;
    }
}

/** Add the optional Impersonate JSON header to an outgoing request. Shared between legacy
 *  pipe-Auth and JWT Bearer paths — impersonation is independent of how the caller authenticates. */
function applyImpersonationHeader(headers: Record<string, string>): void {
  const imp = impersonationAccessor?.();
  if (!imp) return;
  const impHeader: Record<string, unknown> = {};
  if (imp.mode === "user") {
    impHeader.ImpersonateUID = imp.impersonateUID;
  } else {
    if (imp.overrideOperatorID != null) impHeader.OverrideOperatorID = imp.overrideOperatorID;
    if (imp.overrideSuperAdmin != null) impHeader.OverrideSuperAdmin = imp.overrideSuperAdmin;
    if (imp.overrideIngressGroups?.length) impHeader.OverrideIngressGroups = imp.overrideIngressGroups;
    if (imp.overrideUserGroups?.length) impHeader.OverrideUserGroups = imp.overrideUserGroups;
    if (__DEV_FEATURES__ && imp.overrideIPAddress) impHeader.OverrideIPAddress = imp.overrideIPAddress;
  }
  if (Object.keys(impHeader).length > 0) {
    headers["Impersonate"] = JSON.stringify(impHeader);
  }
}

function postProcessResult<T>(result: ReturnObject<T>, method: string, args?: Record<string, unknown>): void {
  handleCommonErrors(result);
  if (!result) return;   // null body (unknown method, backend exception before envelope wrap, etc.)
  if (onUserQueryCallback && result.oData != null && typeof result.oData === "object" && "userQuery" in result.oData) {
    const uq = (result.oData as Record<string, unknown>).userQuery;
    if (typeof uq === "string") {
      onUserQueryCallback(uq, method, args);
    }
  }
}

/* ── Dev-only duplicate-request detector ───────────────────────────────
 *
 * StrictMode double-mounts in dev re-fire `useEffect` and any unguarded
 * fetch-on-open pattern issues the same RPC twice (see
 * `transactionOpsModal.tsx` + `editCartModal.tsx` for the dedupe pattern).
 * To catch missed instances early, every servicePost increments an
 * in-flight counter keyed by `method + JSON(args)`. A second start while
 * the first hasn't settled logs and pauses the debugger.
 *
 * Production builds tree-shake this entirely (`__DEV_FEATURES__` is a
 * constant `false`). Suppress per-call by passing `{ allowDuplicate: true }`
 * in `noteOpts` if you actually want concurrent identical calls (rare:
 * polling, intentional fan-out).
 */
const _inFlightCounts: Map<string, number> = new Map();
const _stableStringify = (v: unknown): string => {
  try { return JSON.stringify(v ?? {}); } catch { return "<unserialisable>"; }
};
function devNoteRequestStart(method: string, args?: Record<string, unknown>): string | null {
  if (!__DEV_FEATURES__) return null;
  const key = `${method}:${_stableStringify(args)}`;
  const next = (_inFlightCounts.get(key) ?? 0) + 1;
  _inFlightCounts.set(key, next);
  if (next > 1) {
    // eslint-disable-next-line no-console
    console.warn(
      `[servicePost] Duplicate in-flight request: ${method}\n` +
      `args: ${_stableStringify(args)}\n` +
      `Likely a StrictMode double-mount, missing useEffect dep, or a churning useCallback identity.\n` +
      `Fix: dedupe via an inFlightRef (see transactionOpsModal.tsx) or an AbortController on cleanup.`
    );
    // eslint-disable-next-line no-debugger
    // debugger;
  }
  return key;
}
function devNoteRequestEnd(key: string | null): void {
  if (!__DEV_FEATURES__ || !key) return;
  const next = (_inFlightCounts.get(key) ?? 1) - 1;
  if (next <= 0) _inFlightCounts.delete(key);
  else _inFlightCounts.set(key, next);
}

async function servicePostCore<T>(
  session: Session,
  method: string,
  args?: Record<string, unknown>,
  signal?: AbortSignal
): Promise<ReturnObject<T>> {
  assertEndpointForMethod(method, session.ActiveEndPointID);
  const body = JSON.stringify({ method, args: args ?? undefined });
  const url = "/api/v2/service";
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Auth: buildAuthHeader(session),
  };
  applyImpersonationHeader(headers);
  const dupeKey = devNoteRequestStart(method, args);
  try {
    const response = await fetch(buildServiceUrl(url), { method: "POST", headers, body, signal: withEndpointSignal(signal) });
    if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
    const result = (await response.json()) as ReturnObject<T>;
    postProcessResult(result, method, args);
    return result;
  } finally {
    devNoteRequestEnd(dupeKey);
  }
}

/** JWT-flavored RPC. Authorization: Bearer + X-Active-Endpoint header. */
async function servicePostOAuthCore<T>(
  access: string,
  endpointId: number,
  method: string,
  args?: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<ReturnObject<T>> {
  assertEndpointForMethod(method, endpointId);
  const body = JSON.stringify({ method, args: args ?? undefined });
  const url = "/api/v2/service";
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${access}`,
    "X-Active-Endpoint": String(endpointId),
  };
  applyImpersonationHeader(headers);
  const dupeKey = devNoteRequestStart(method, args);
  try {
    const response = await fetch(buildServiceUrl(url), { method: "POST", headers, body, signal: withEndpointSignal(signal) });
    if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
    const result = (await response.json()) as ReturnObject<T>;
    postProcessResult(result, method, args);
    return result;
  } finally {
    devNoteRequestEnd(dupeKey);
  }
}

/** When cockpit JWT tokens are present, return a result via the Bearer/X-Active-Endpoint path
 *  and ignore the supplied (synth or real) Session entirely. Both servicePost and
 *  servicePostWithSession route through this so WebInitCache, file-key requests, etc. all use
 *  JWT auth uniformly when opted in. */
async function tryCockpitJwtPath<T>(
  method: string,
  args?: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<ReturnObject<T> | null> {
  const { getTokens, getAccessToken, getActiveEndpointId } = await import("../auth/cockpitJwt.ts");
  const tokens = getTokens();
  if (!tokens) return null;
  const access = await getAccessToken();
  if (!access) {
    onSessionExpiredCallback?.("refresh-failed");
    throw new Error("Cockpit JWT refresh failed");
  }
  return servicePostOAuthCore<T>(access, getActiveEndpointId(), method, args, signal);
}

export async function servicePost<T>(
  method: string,
  args?: Record<string, unknown>,
  signal?: AbortSignal
): Promise<ReturnObject<T>> {
  const jwt = await tryCockpitJwtPath<T>(method, args, signal);
  if (jwt) return jwt;
  const session = sessionAccessor?.();
  if (!session) {
    throw new Error("No active session");
  }
  return servicePostCore(session, method, args, signal);
}

export async function servicePostWithSession<T>(
  session: Session,
  method: string,
  args?: Record<string, unknown>,
  signal?: AbortSignal
): Promise<ReturnObject<T>> {
  const jwt = await tryCockpitJwtPath<T>(method, args, signal);
  if (jwt) return jwt;
  return servicePostCore(session, method, args, signal);
}

/** One sub-call in a batch sent to `/api/v2/services`. */
export interface BatchCall {
  method: string;
  args?: Record<string, unknown>;
}

/**
 * Bulk RPC — POSTs a single request to `/api/v2/services` with N sub-calls,
 * server parses Auth/JWT once and runs them in parallel (cap 8), returns
 * `ReturnObject<unknown>[]` in the same order as the input.
 *
 * Use when a page boot-loads 3+ small payloads from the same session — the
 * cost is one HTTP/TLS handshake + one JWT validation regardless of N.
 * Each sub-call still goes through the per-method `IPermissionAttribute`
 * check; one failing sub-call is reported in its own envelope (`bError:true`)
 * but does not affect peers. Sub-calls run concurrently — only batch
 * mutations that don't write the same row.
 *
 * Result is typed as a generic tuple so callers can name each sub-call's
 * payload type. Arg names must match the VB parameter name (case-insensitive
 * but exact otherwise — Hungarian prefixes count, e.g. `iTransactionID` ≠
 * `transactionID`):
 * ```ts
 * const [info, head, paymentLogs] = await servicePostBatch<[Info, Head, PaymentLog[]]>([
 *   { method: "TransactionInformation",      args: { transactionID } },
 *   { method: "TransactionHead",             args: { transactionID } },
 *   { method: "WebGetTransactionPaymentIDs", args: { iTransactionID: transactionID } },
 * ]);
 * if (info.bError) ...
 * ```
 */
export async function servicePostBatch<TResults extends unknown[] = unknown[]>(
  calls: BatchCall[],
  signal?: AbortSignal,
): Promise<{ [K in keyof TResults]: ReturnObject<TResults[K]> }> {
  if (calls.length === 0) {
    return [] as unknown as { [K in keyof TResults]: ReturnObject<TResults[K]> };
  }
  const body = JSON.stringify({ calls });

  let headers: Record<string, string>;
  // Mirror servicePost's auth resolution: prefer JWT when cockpit tokens are
  // present, otherwise fall back to the Session-based Auth header.
  const { getTokens, getAccessToken, getActiveEndpointId } = await import("../auth/cockpitJwt.ts");
  const tokens = getTokens();
  if (tokens) {
    const access = await getAccessToken();
    if (!access) {
      onSessionExpiredCallback?.("refresh-failed");
      throw new Error("Cockpit JWT refresh failed");
    }
    const endpointId = getActiveEndpointId();
    for (const c of calls) assertEndpointForMethod(c.method, endpointId);
    headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${access}`,
      "X-Active-Endpoint": String(endpointId),
    };
  } else {
    const session = sessionAccessor?.();
    if (!session) throw new Error("No active session");
    for (const c of calls) assertEndpointForMethod(c.method, session.ActiveEndPointID);
    headers = {
      "Content-Type": "application/json",
      Auth: buildAuthHeader(session),
    };
  }
  applyImpersonationHeader(headers);

  // Reuse the existing duplicate-request detector — one entry per sub-call
  // method so a batch that internally repeats a method still flags. Registered
  // after auth/endpoint resolution so a suppressed batch doesn't leak counters.
  const dupeKeys = calls.map(c => devNoteRequestStart(c.method, c.args));

  try {
    const response = await fetch(buildServiceUrl("/api/v2/services"), {
      method: "POST",
      headers,
      body,
      signal: withEndpointSignal(signal),
    });
    if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
    const envelope = (await response.json()) as { results: ReturnObject<unknown>[] };
    const results = envelope.results ?? [];
    // Apply postProcessResult per slot so global error handlers (session
    // expired, IP restriction, etc.) fire for each underlying call.
    for (let i = 0; i < results.length; i++) {
      const result = results[i];
      const call = calls[i];
      if (result && call) postProcessResult(result, call.method, call.args);
    }
    return results as { [K in keyof TResults]: ReturnObject<TResults[K]> };
  } finally {
    for (const key of dupeKeys) devNoteRequestEnd(key);
  }
}

/**
 * Streaming sibling of {@link servicePostBatch}. POSTs the same `{ calls }` body to
 * `/api/v2/services-stream`, which responds as NDJSON — one `{ index, result }` line
 * flushed as each sub-call completes. `onResult` fires per line as it arrives, so the
 * caller can render each slot progressively instead of awaiting the slowest call.
 *
 * Resolves once the stream ends. Whether chunks actually arrive incrementally depends
 * on every hop in the path (Kestrel, nginx, WAF) honouring flushes — the SuperAdmin
 * "Test Response Streaming" tool measures this end-to-end.
 *
 * `onResult` receives the original input index and that slot's `ReturnObject`. Auth
 * resolution (JWT vs legacy pipe-Auth) mirrors `servicePostBatch`.
 */
export async function servicePostBatchStream(
  calls: BatchCall[],
  onResult: (index: number, result: ReturnObject<unknown>) => void,
  signal?: AbortSignal,
): Promise<void> {
  if (calls.length === 0) return;
  const body = JSON.stringify({ calls });

  let headers: Record<string, string>;
  const { getTokens, getAccessToken, getActiveEndpointId } = await import("../auth/cockpitJwt.ts");
  const tokens = getTokens();
  if (tokens) {
    const access = await getAccessToken();
    if (!access) {
      onSessionExpiredCallback?.("refresh-failed");
      throw new Error("Cockpit JWT refresh failed");
    }
    const endpointId = getActiveEndpointId();
    for (const c of calls) assertEndpointForMethod(c.method, endpointId);
    headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${access}`,
      "X-Active-Endpoint": String(endpointId),
    };
  } else {
    const session = sessionAccessor?.();
    if (!session) throw new Error("No active session");
    for (const c of calls) assertEndpointForMethod(c.method, session.ActiveEndPointID);
    headers = {
      "Content-Type": "application/json",
      Auth: buildAuthHeader(session),
    };
  }
  applyImpersonationHeader(headers);

  const response = await fetch(buildServiceUrl("/api/v2/services-stream"), {
    method: "POST",
    headers,
    body,
    signal: withEndpointSignal(signal),
  });
  if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
  if (!response.body) throw new Error("Streaming not supported: response body is null");

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const dispatchLine = (line: string) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    try {
      const parsed = JSON.parse(trimmed) as { index: number; result: ReturnObject<unknown> };
      if (typeof parsed.index === "number" && parsed.result) {
        postProcessResult(parsed.result, calls[parsed.index]?.method ?? "(stream)", calls[parsed.index]?.args);
        onResult(parsed.index, parsed.result);
      }
    } catch {
      // Partial/garbled line — skip; a complete line will follow on the next read.
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buffer.indexOf("\n")) >= 0) {
      const line = buffer.slice(0, nl);
      buffer = buffer.slice(nl + 1);
      dispatchLine(line);
    }
  }
  // Flush any trailing line without a terminating newline.
  buffer += decoder.decode();
  dispatchLine(buffer);
}

export async function servicePostOAuth<T>(
  method: string,
  oauthToken: string,
  args?: Record<string, unknown>
): Promise<ReturnObject<T>> {
  const body = JSON.stringify({ method, args: args ?? undefined });
  const url = "/api/v2/service";

  const response = await fetch(buildServiceUrl(url), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${oauthToken}`,
    },
    body,
  });

  if (!response.ok) {
    throw new Error(`HTTP error: ${response.status}`);
  }

  const result = (await response.json()) as ReturnObject<T>;
  handleCommonErrors(result);
  return result;
}

/**
 * File-access key flow — non-RPC endpoint at `/api/v2/files/requestKey`
 * (see `Program.vb` fileAccessKeyHandler + `Infrastructure/FileAccess.vb`
 * `RequestFileKey`). Returns a short-lived key the caller combines with the
 * GET URL: `${HostAddress}/api/v2/files/request/{path}/{fileType}/{key}`.
 *
 * `fileType` values (from `RequestFileData.FileTypes` enum):
 *   1 = ExportCSV, 2 = ErrorVideo, 3 = Media, 4 = ProductImage, 11 = Golf, 12 = Access
 *
 * Returns `null` if the session is invalid (server returns a null JSON literal).
 */
export async function requestFileKey(path: string, fileType: number): Promise<string | null> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  // JWT path: Authorization Bearer + X-Active-Endpoint, mirroring servicePost. Falls back to
  // legacy pipe-Auth when no cockpit tokens.
  const { getTokens, getAccessToken, getActiveEndpointId } = await import("../auth/cockpitJwt.ts");
  const tokens = getTokens();
  if (tokens) {
    const access = await getAccessToken();
    if (!access) {
      onSessionExpiredCallback?.("refresh-failed");
      throw new Error("Cockpit JWT refresh failed");
    }
    const endpointId = getActiveEndpointId();
    assertEndpointForMethod("requestFileKey", endpointId);
    headers["Authorization"] = `Bearer ${access}`;
    headers["X-Active-Endpoint"] = String(endpointId);
  } else {
    const session = sessionAccessor?.();
    if (!session) throw new Error("No active session");
    assertEndpointForMethod("requestFileKey", session.ActiveEndPointID);
    headers["Auth"] = buildAuthHeader(session);
  }
  const response = await fetch(buildServiceUrl("/api/v2/files/requestKey"), {
    method: "POST",
    headers,
    body: JSON.stringify({ Path: path, FileType: fileType }),
    signal: withEndpointSignal(),
  });
  if (!response.ok) {
    throw new Error(`requestFileKey HTTP error: ${response.status}`);
  }
  const raw = (await response.json()) as string | null;
  return raw && raw.length > 0 ? raw : null;
}

export async function exchangeSignInToken(token: string): Promise<Session> {
  const url = "/api/v2/signin-token";
  const body = token;
  const headers: Record<string, string> = { "Content-Type": "text/plain" };
  // DEBUG-only: forward override IP at sign-in so FilterIPRestriction (called inside ConsumeSignInServiceToken
  // → WebInstantLoginFillSession) uses impersonated IP. Read from dedicated localStorage key — runtime
  // impersonation state is wiped by the SignInService redirect roundtrip. Backend honours only when
  // the resolved user is a real super-admin.
  if (__DEV_FEATURES__) {
    const { getSignInOverrideIP } = await import("../auth/signInOverride.ts");
    const ip = getSignInOverrideIP();
    if (ip) headers["Impersonate"] = JSON.stringify({ OverrideIPAddress: ip });
  }
  const response = await fetch(buildServiceUrl(url), {
    method: "POST",
    headers,
    body,
  });

  if (!response.ok) {
    throw new Error(`Token exchange failed: ${response.status}`);
  }

  return (await response.json()) as Session;
}
